import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import React, { useEffect } from 'react';

import { colors, spacing } from './KestrixDesignSystem.jsx';
import KestrixTypography from './KestrixTypography.jsx';

/**
 * KestrixToast component for displaying toast notifications.
 *
 * This component displays a toast notification with an icon and message.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the toast is visible
 * @param {string} props.message - The message to display
 * @param {string} [props.type='info'] - The type of toast (success, error, info, warning)
 * @param {number} [props.duration=3000] - Duration in milliseconds before auto-hide
 * @param {Function} [props.onClose] - Function to call when toast is closed
 * @param {string} [props.testId] - Test ID for the toast
 * @returns {JSX.Element} KestrixToast component
 */
const KestrixToast = ({
  open,
  message,
  type = 'info',
  duration = 3000,
  onClose,
  testId = '',
}) => {
  // Auto-hide timer effect
  useEffect(() => {
    if (open && duration > 0) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [open, duration, onClose]);

  if (!open) return null;

  const getIconColor = () => {
    switch (type) {
      case 'success':
        return colors.semantic.success.main;
      case 'error':
        return colors.semantic.error.main;
      case 'warning':
        return colors.semantic.warning.main;
      case 'info':
        return colors.base.KestrixWhite;
      case 'loading':
        return colors.neutral[600];
      default:
        return colors.text.primary;
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z'
              stroke='white'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        );
      case 'error':
        return (
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M15 9L9 15M9 9L15 15M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z'
              stroke='white'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        );
      case 'warning':
        return (
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M12 9V13M12 17H12.01M10.29 3.86003L1.82 18C1.64537 18.3024 1.55297 18.6453 1.552 18.9945C1.55103 19.3438 1.64153 19.6871 1.81445 19.9905C1.98737 20.2939 2.23675 20.5467 2.53773 20.7239C2.83871 20.901 3.18082 20.9962 3.53 21H20.47C20.8192 20.9962 21.1613 20.901 21.4623 20.7239C21.7633 20.5467 22.0126 20.2939 22.1856 19.9905C22.3585 19.6871 22.449 19.3438 22.448 18.9945C22.4471 18.6453 22.3547 18.3024 22.18 18L13.71 3.86003C13.5328 3.56613 13.2817 3.32312 12.9818 3.15451C12.6819 2.9859 12.3431 2.89728 12 2.89728C11.6569 2.89728 11.3181 2.9859 11.0182 3.15451C10.7183 3.32312 10.4672 3.56613 10.29 3.86003Z'
              stroke='white'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        );
      default:
        return (
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z'
              stroke={type === 'info' ? 'black' : 'white'}
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        );
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 9999,
        minWidth: 200,
        maxWidth: 400,
        padding: `${spacing[3]} ${spacing[4]}`,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: getIconColor(),
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${colors.border.light}`,
        animation: 'slideInFromRight 0.3s ease-out',
        '@keyframes slideInFromRight': {
          '0%': {
            transform: 'translateX(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateX(0)',
            opacity: 1,
          },
        },
      }}
      data-testid={testId}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: 'none',
        }}
      >
        <Box
          sx={{
            color: type === 'info' ? 'black' : 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getIcon()}
        </Box>
      </Box>

      {/* Message */}
      <KestrixTypography
        variant='body2'
        sx={{
          color: type === 'info' ? 'black' : 'white',
          flex: 1,
        }}
      >
        {message}
      </KestrixTypography>
    </Paper>
  );
};

export default KestrixToast;
